import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ethers } from 'ethers';
import { getContractByAddress, Deposit } from '../contracts/contract';
import { useNetwork } from '../contexts/NetworkContext';
import { useWallet } from '../contexts/WalletContext';
import { useEscrow } from '../contexts/EscrowContext';
import Header from './Header';
import DepositList from './DepositList';
import './WithdrawPage.css';

const WithdrawPage: React.FC = () => {
  const navigate = useNavigate();
  const { currentNetwork } = useNetwork();
  const { provider, signer, account, isConnected, onAccountChange } = useWallet();
  const { isAddressSet, contractAddress } = useEscrow();
  const [, setDeposits] = useState<Deposit[]>([]);
  const [filteredDeposits, setFilteredDeposits] = useState<Deposit[]>([]);
  const [, setDepositCounter] = useState<number>(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [arbiterAddress, setArbiterAddress] = useState<string>('');
  
  // Smart auto-refresh state
  const [autoRefreshActive, setAutoRefreshActive] = useState(false);
  const [expectedDepositCount, setExpectedDepositCount] = useState<number | null>(null);
  const [expectedDepositStates, setExpectedDepositStates] = useState<Map<number, number> | null>(null);

  // Filter deposits to show only those where current user is beneficiary
  const filterDeposits = useCallback((depositList: Deposit[], userAddress: string) => {
    return depositList.filter(deposit => 
      deposit.beneficiary.toLowerCase() === userAddress.toLowerCase()
    );
  }, []);

  // Check if expected changes have occurred
  const checkExpectedChanges = useCallback((depositList: Deposit[]): boolean => {
    if (!expectedDepositCount && !expectedDepositStates) {
      return false;
    }

    // Check if deposit count changed as expected
    if (expectedDepositCount !== null && depositList.length >= expectedDepositCount) {
      return true;
    }

    // Check if specific deposit states changed as expected
    if (expectedDepositStates) {
      for (const [depositId, expectedState] of Array.from(expectedDepositStates.entries())) {
        if (depositList[depositId] && depositList[depositId].state === expectedState) {
          return true;
        }
      }
    }

    return false;
  }, [expectedDepositCount, expectedDepositStates]);

  const loadArbiterAddress = useCallback(async (provider: ethers.BrowserProvider) => {
    if (!contractAddress) return;
    
    try {
      const contract = getContractByAddress(provider, contractAddress);
      const arbiter = await contract.arbiter();
      setArbiterAddress(arbiter);
    } catch (err) {
      console.error('Failed to load arbiter address:', err);
    }
  }, [contractAddress]);

  const loadDeposits = useCallback(async (provider: ethers.BrowserProvider) => {
    if (!currentNetwork || !contractAddress) {
      setError('No network selected or contract address not set');
      return;
    }
    
    if (!account) {
      setError('Account not connected');
      return;
    }

    try {
      setLoading(true);
      const contract = getContractByAddress(provider, contractAddress);
      const counter = await contract.depositCounter();
      setDepositCounter(Number(counter));
      
      const depositList: Deposit[] = [];
      for (let i = 0; i < Number(counter); i++) {
        const deposit = await contract.deposits(i);
        depositList.push({
          id: i, // Store the actual deposit ID from the contract
          depositor: deposit.depositor,
          beneficiary: deposit.beneficiary,
          amount: deposit.amount.toString(),
          token: deposit.token,
          state: Number(deposit.state),
          depositTimestamp: deposit.depositTimestamp ? deposit.depositTimestamp.toString() : '0',
          completionTimestamp: deposit.completionTimestamp ? deposit.completionTimestamp.toString() : '0'
        });
      }
      setDeposits(depositList);
      
      // Filter deposits for current user as beneficiary
      const userDeposits = filterDeposits(depositList, account);
      setFilteredDeposits(userDeposits);
      
      // Check if expected changes have occurred
      if (autoRefreshActive) {
        const changesDetected = checkExpectedChanges(depositList);
        if (changesDetected) {
          console.log('Expected changes detected, stopping auto-refresh');
          setAutoRefreshActive(false);
          setExpectedDepositCount(null);
          setExpectedDepositStates(null);
        }
      }
    } catch (err) {
      setError('Failed to load deposits: ' + (err as Error).message);
    } finally {
      setLoading(false);
    }
  }, [currentNetwork, contractAddress, account, autoRefreshActive, checkExpectedChanges, filterDeposits]);

  // Start smart auto-refresh after successful operations
  const startSmartRefresh = (expectedCount?: number, expectedStates?: Map<number, number>) => {
    console.log('Starting smart refresh after successful operation');
    setExpectedDepositCount(expectedCount || null);
    setExpectedDepositStates(expectedStates || null);
    setAutoRefreshActive(true);
  };

  // Smart refresh callbacks for different operations
  const onActionSuccess = async (depositId: number, expectedState: number) => {
    // Expect specific deposit state to change
    const expectedStates = new Map<number, number>();
    expectedStates.set(depositId, expectedState);
    startSmartRefresh(undefined, expectedStates);
  };

  // Navigate to homepage if not connected or no escrow address
  useEffect(() => {
    if (!isConnected || !isAddressSet) {
      navigate('/');
      return;
    }
  }, [isConnected, isAddressSet, navigate]);

  // Listen for account changes and redirect to homepage
  useEffect(() => {
    const unsubscribe = onAccountChange((newAccount) => {
      // Redirect to homepage when account changes
      navigate('/');
    });
    
    return unsubscribe;
  }, [onAccountChange, navigate]);

  // Smart auto-refresh: only when active and after successful operations
  useEffect(() => {
    if (!autoRefreshActive || !isConnected || !provider || !currentNetwork) {
      return;
    }

    console.log('Starting smart auto-refresh (5s interval)...');
    loadDeposits(provider);
    const interval = setInterval(() => {
      console.log('Auto-refreshing deposits...');
      loadDeposits(provider);
    }, 5000); // 5 seconds

    return () => {
      console.log('Stopping smart auto-refresh');
      clearInterval(interval);
    };
  }, [autoRefreshActive, isConnected, provider, currentNetwork, loadDeposits]);

  // Load deposits when wallet connects
  useEffect(() => {
    if (provider && account && currentNetwork) {
      setError(''); // Clear any previous errors
      loadDeposits(provider);
      loadArbiterAddress(provider);
    }
  }, [provider, account, currentNetwork, loadDeposits, loadArbiterAddress]);

  return (
    <div className="withdraw-page">
      <Header subtitle="Withdraw Funds" />
      
      <main className="container">
        <div className="main-content">
            
            {error && <div className="error">{error}</div>}
            
            {currentNetwork ? (
              <>
                <div className="page-description">
                  <h3>Your Deposits</h3>
                  <p>View and withdraw from deposits where you are the beneficiary</p>
                </div>
                
                {autoRefreshActive && (
                  <div className="smart-refresh-notification">
                    <span className="refresh-icon">🔄</span>
                    Please wait a moment for the deposit list to reflect the latest changes...
                  </div>
                )}
                
                <DepositList 
                  deposits={filteredDeposits}
                  signer={signer!}
                  network={currentNetwork}
                  userAddress={account}
                  provider={provider!}
                  contractAddress={contractAddress!}
                  arbiterAddress={arbiterAddress}
                  onAction={onActionSuccess}
                />
              </>
            ) : (
              <div className="no-network">
                <h3>No Network Selected</h3>
                <p>Please select a supported network to continue.</p>
              </div>
            )}
            
            <div className="loading" style={{ visibility: loading ? 'visible' : 'hidden' }}>
              Loading deposits...
            </div>
        </div>
      </main>
      
      <footer>
        <p>Contact us: <a href="mailto:contact@trustline.id">contact@trustline.id</a></p>
      </footer>
    </div>
  );
};

export default WithdrawPage;
